import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { isExpired } from "react-jwt";
import { Outlet, useLocation } from "react-router-dom";
import {
  getSpecifyRelatedResourceList,
  apiGetTenant,
} from "../api/admin";
import { getURLDomain, logoutFunction } from "../common/utils";
import Loading from "../components/Loading";
import {
  RIMS_LOCAL_STG_CONSTANTS,
  pathAuthMapping,
  pathAuthWhiteList,
} from "../constants";
import { useCookieData } from "../hooks/useCookieData";
import { useTenantSettingsLoader } from "../hooks/useTenantSettingsLoader";
import { useConfigStore } from "../store/config";
import styles from "./index.module.scss";
import useGlobalFeatureFlagStore, {
  type GlobalFeatureFlags,
} from "../store/globalFeatureFlag";
import { parseTenantOnboardingResponseToGlobalFeatureFlags } from "../common/parseTenantOnboarding";
import { useQuery } from "@tanstack/react-query";
import useGlobalLoading from "../store/globalLoading";
import { useAppIsFetching } from "../hooks/useAppIsFetching";
import classnames from "classnames";
import { useSideMenuExpandStore } from "../store/menu";
import { useTranslation } from "react-i18next";
import {
  loadMuiThemeLocales,
  normalizeMuiLanguage,
  type MuiThemeLocales,
} from "../utils/muiLocaleLoader";
const FreyrComponentLibraryProvider = lazy(() =>
  import("@freyr/freyr-react-common-components").then((module) => ({
    default: module.FreyrComponentLibraryProvider,
  })),
);

const ESignValidationWapper = lazy(
  () => import("../pages/product/components/ESignValidationWapper"),
);

const Layouts = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [auth, setAuth] = useState<boolean>(true);
  const [authResolved, setAuthResolved] = useState<boolean>(false);
  const [language] = useConfigStore((state) => [state.language]);
  const theme = useTheme();
  const { globalLoading } = useGlobalLoading();
  const URLDomain = getURLDomain();
  const redirectURL = `https://portal.${URLDomain}`;
  const location = useLocation();
  const { setGlobalFeatureFlags } = useGlobalFeatureFlagStore();
  const { token, userId, tenantId, domain, userEmail, userInfo } =
    useCookieData();

  useTenantSettingsLoader({ enabled: !!tenantId });

  const [muiThemeLocales, setMuiThemeLocales] =
    useState<MuiThemeLocales | null>(null);
  const muiLocaleGenRef = useRef(0);

  useEffect(() => {
    muiLocaleGenRef.current += 1;
    const gen = muiLocaleGenRef.current;
    const lang = normalizeMuiLanguage(language);
    let cancelled = false;

    loadMuiThemeLocales(lang).then((locales) => {
      if (!cancelled && gen === muiLocaleGenRef.current) {
        setMuiThemeLocales(locales);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [language]);

  const themeWithLocale = useMemo(() => {
    if (!muiThemeLocales) {
      return theme;
    }

    const {
      core: coreLocale,
      pickers: pickersLocale,
      grid: gridLocale,
    } = muiThemeLocales;

    return createTheme(
      {
        palette: {},
        components: {
          MuiFormControl: {
            styleOverrides: {
              root: {
                ".MuiDataGrid-toolbarContainer &": {
                  width: "160px",
                  flex: "1 1 auto",
                  maxWidth: "50%",
                },
              },
            },
          },
        },
      },
      coreLocale,
      pickersLocale,
      gridLocale,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muiThemeLocales, theme]);

  const getAuth = (pathname: string) => {
    // make '/products/edit-product/12100693'and  /products/edit-product/12100693/route and /products/edit-product/12100693/route/10001 =true
    const exactMatch = pathAuthMapping.get(pathname);
    if (exactMatch) return exactMatch;

    const pathParts = pathname.split("/");
    while (pathParts.length > 2) {
      pathParts.pop();
      const wildcardPath = pathParts.join("/") + "/*";
      const match = pathAuthMapping.get(wildcardPath);
      if (match) return match;
    }

    return null;
  };

  /** Tenant onboarded modules (subscription by tenantId); not the dedicated feature-flag API. */
  const {
    data: globalFeatureFlagsFromOnboarding,
    isError: tenantOnboardingQueryError,
  } = useQuery({
    queryKey: ["tenantOnboardedModules", tenantId],
    queryFn: () => apiGetTenant(tenantId!),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    retry: false,
    select: (res) => parseTenantOnboardingResponseToGlobalFeatureFlags(res),
  });

  /** Merge all tenant-scoped queries into one patch; add new sources here instead of new useEffects. */
  const globalFeatureFlagPatch = useMemo((): Partial<GlobalFeatureFlags> => {
    const patch: Partial<GlobalFeatureFlags> = {};
    if (tenantOnboardingQueryError && tenantId) {
      patch.enableServerPagination = true;
    } else if (globalFeatureFlagsFromOnboarding !== undefined) {
      Object.assign(patch, globalFeatureFlagsFromOnboarding);
    }
    return patch;
  }, [tenantOnboardingQueryError, tenantId, globalFeatureFlagsFromOnboarding]);

  useEffect(() => {
    if (Object.keys(globalFeatureFlagPatch).length > 0) {
      setGlobalFeatureFlags(globalFeatureFlagPatch);
    }
  }, [globalFeatureFlagPatch, setGlobalFeatureFlags]);

  useEffect(() => {
    if (token === null || isExpired(token)) {
      setAuth(false);
      setAuthResolved(true);
      if (process.env.NODE_ENV === "production") {
        logoutFunction();
        window.location.href = `${redirectURL}?redirect=${window.location.origin}`;
      }
    } else {
      if (userId) {
        getSpecifyRelatedResourceList({
          userId: userId,
          resourceType: "menu",
          app: "",
          module: "7",
          subModule: "",
          parentId: null,
        })
          .then((data: any) => {
            const handleData = data?.data?.map((o: Record<string, any>) => ({
              name: o.name,
              parentName: o.parentName,
              displayName: o.displayName,
            }));
            const mappingData = handleData
              .map((i: { name: string; parentName: string }) => i.name)
              .includes(getAuth(location.pathname));
            if (pathAuthWhiteList.includes(location.pathname) || mappingData) {
              window.localStorage.setItem(
                RIMS_LOCAL_STG_CONSTANTS.MENU_AUTH_LIST,
                JSON.stringify(handleData),
              );
              setAuth(true);
            } else {
              setAuth(false);
              enqueueSnackbar("You have no privileges.", { variant: "error" });
            }
          })
          .catch((err: any) => {
            setAuth(false);
          })
          .finally(() => {
            setAuthResolved(true);
          });
          setAuthResolved(true);
      } else {
        setAuth(false);
        setAuthResolved(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noPaddingPath = [
    "/global-search-list",
    "/associate-process",
    "/initiate-process",
  ];

  const hasNopadding = noPaddingPath.some((i) =>
    window.location.pathname.includes(i),
  );
  const { expand } = useSideMenuExpandStore();
  const isFetching = useAppIsFetching();
  const isProductRoute = location.pathname.startsWith("/products");
  const outletFallback = (
    <div className={styles["outlet-suspense-fallback"]}>
      {t("common.loadingText")}
    </div>
  );
  const layoutBody = (showOutlet: boolean) => (
    <div className={styles["layout"]}>
      {/* <Stack flexDirection="row" sx={{ flex: 1, overflowY: "auto" }}> */}
      <section className="layout-right">
        <div className="page-wrapper" style={{ position: "relative" }}>
          <div
            className={classnames({
              "global-search-custom-page": hasNopadding,
              "custom-page": !hasNopadding,
              [styles["no-side-menu"]]:
                expand && location.pathname !== "/products",
            })}
          >
            {showOutlet ? (
              <Suspense fallback={outletFallback}>
                <Outlet />
              </Suspense>
            ) : (
              outletFallback
            )}
          </div>
        </div>
      </section>
    </div>
  );
  const isAuthPending = !authResolved;
  const shouldRenderLayout = auth || isAuthPending;
  return (
    <>
      {shouldRenderLayout && (
        <ThemeProvider theme={themeWithLocale}>
          {isProductRoute && auth ? (
            <Suspense fallback={layoutBody(false)}>
              <FreyrComponentLibraryProvider
                token={token}
                language={language!}
                tenant={tenantId}
                domain={"1"}
                domainName={domain}
                useEmail={userEmail}
                envVars={{
                  IM_PREFIX: process.env.REACT_APP_API_GETWAY,
                  URF_PREFIX: process.env.REACT_APP_API_GETWAY,
                }}
                moduleName="Product"
                userInfo={btoa(encodeURIComponent(JSON.stringify(userInfo)))}
              >
                {layoutBody(true)}
                <ESignValidationWapper />
              </FreyrComponentLibraryProvider>
            </Suspense>
          ) : (
            layoutBody(auth)
          )}
          <Loading open={globalLoading || isFetching > 0} />
        </ThemeProvider>
      )}
    </>
  );
};

export default Layouts;
